import matchers from "../../js/validate-props/matchers";

const topics = {};

export const invalidTopicMsg =
    "The topic (String or HTMLElement) supplied to the DDS Broker was not found!";
export const invalidCallbackMsg =
    "The callback supplied to the DDS Broker subscribe method was not a function!";

const getName = (topic) => {
    if (topic) {
        if (matchers.string(topic)) return topic;
        if (matchers.htmlElement(topic) && topic.hasAttribute("id"))
            return topic.getAttribute("id");
    }
    console.warn(invalidTopicMsg);
    return false;
};

const validateCallback = (callback) => {
    if (matchers.function(callback)) {
        return callback;
    }
    console.warn(invalidCallbackMsg);
    return false;
};

/**
 * Method used to publish Events to a specified topic. The topic can be
 * a String or it can be extracted from an HTMLElement's id attribute. If
 * no topic is found then the method will console a warning and return.
 *
 * The method creates a CustomEvent using the extracted name. The CustomEvent will
 * be dispatched including two properties:
 * detail - the data being published or empty object.
 * relatedTarget - the publisher (HTMLElement) that published the data or undefined.
 *
 * NOTE: if using a String for the topic name and no relatedTarget, the property will
 * be undefined.
 *
 * @param {String|HTMLElement} topic - required
 * @param {Object} data - optional
 * @param {HTMLElement} relatedTarget - optional - if using String based topic name
 */
export const publish = (topic, data = {}, relatedTarget = undefined) => {
    const name = getName(topic);
    if (!name) return false;
    topics[name] = new CustomEvent(name, { detail: data });
    topics[name].relatedTarget = matchers.htmlElement(topic) ? topic : relatedTarget;
    dispatchEvent(topics[name]);
};

/**
 * Method used to subscribe to a Publishers specified topic. The topic can be a String
 * or it can be the publisher HTMLElement. If it is an HTMLElement the topic name will
 * be extracted from the HTMLElement's id attribute. If no topic is found then the method
 * will console a warning and return.
 *
 * The method creates and EventListener based on topic name and callback handler. If the
 * callback handler is not a Function the method will console a warning and return. The
 * context can be used to bind scope to the callback handler. This will be needed for callbacks
 * that are not globally accessible functions.
 *
 * NOTE: if the Publisher is using a String for the topic name and no relatedTarget, the property
 * will be undefined.
 *
 * @param {String|HTMLElement} topic - required
 * @param {Function} callback - required
 * @param {String} event - optional - filter topic messages by event type
 * @param {Scope} context - optional - if callback is not global
 * @return {Function} the handler function to be used to unsubscribe
 */
export const subscribe = (topic, callback, event, context) => {
    const name = getName(topic);
    const handler = validateCallback(callback);
    if (!name || !handler) return false;
    const messageHandler = (message) => {
        if (matchers.string(event)) {
            if (message.detail.type === event) {
                handler.bind(context)(message.detail);
            }
        } else {
            handler.bind(context)(message);
        }
    };
    addEventListener(name, messageHandler);
    // Immediately invoke the callback with latest message
    if (topics[name]) messageHandler(topics[name]);
    return messageHandler;
};

/**
 * Method used to unsubscribe to a Publishers specified topic. The topic can be a String
 * or it can be the publisher HTMLElement. If it is an HTMLElement the topic name will
 * be extracted from either the HTMLElement's dataset topic or it will use the HTMLElement's
 * id attribute. If no topic is found then the method will throw an Error.
 *
 * The method calls the remove on the EventListener create during the subscribe function.
 * If the callback handler is not a Function the method will throw an Error. If a context
 * was used to bind scope to the callback handler it will need to be added for the full
 * removal of the EventListener.
 *
 * NOTE: If the subscription was created using a context and none is supplied the siganture
 * callback handler will not match and so will not be removed.
 * @todo implement event
 * @param {String|HTMLElement} topic - required
 * @param {Function} callback - required
 */
export const unsubscribe = (topic, callback) => {
    const name = getName(topic);
    const handler = validateCallback(callback);
    if (!name || !handler) return false;
    removeEventListener(name, handler);
};
